import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { stockReconciliationService } from '../../services/stockReconciliationService';
import { apiService } from '../../services/api';
import { CreateStockReconciliationRequest } from '../../types';
import './StockReconciliationPage.css';
import { parseApiError } from '../../utils/errorUtils';
import { ArrowLeft, Save, AlertCircle, Plus, X } from 'lucide-react';

// ─── Local types ──────────────────────────────────────────────────────────────

interface WarehouseOption { id: number; name: string; }
interface ProductOption   { id: number; name: string; sku: string; }
interface UnitOption      { id: number; unitName: string; }

interface LineItem {
  productId:        number;
  unitId:           number;
  physicalQuantity: number;
  varianceReason:   string;
  notes:            string;
  // edit mode — carry original item id
  stockReconciliationItemId?: number;
}

const RECONCILIATION_TYPES = ['Daily', 'Weekly', 'Monthly', 'Adhoc'] as const;

const emptyLine = (): LineItem => ({
  productId:        0,
  unitId:           0,
  physicalQuantity: 0,
  varianceReason:   '',
  notes:            '',
});

// ─── Component ────────────────────────────────────────────────────────────────

const StockReconciliationCreatePage: React.FC = () => {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id?: string }>();
  const { t }       = useTranslation();
  const { user }    = useAuth();
  const isEdit      = !!id;

  // ── Header fields ──────────────────────────────────────────────────────────

  const [warehouseId,          setWarehouseId]          = useState<number>(0);
  const [reconciliationDate,   setReconciliationDate]   = useState(new Date().toISOString().split('T')[0]);
  const [reconciliationType,   setReconciliationType]   = useState<string>('Adhoc');
  const [notes,                setNotes]                = useState('');

  // ── Line items ─────────────────────────────────────────────────────────────

  const [items, setItems] = useState<LineItem[]>([emptyLine()]);

  // ── Dropdown data ──────────────────────────────────────────────────────────

  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [products,   setProducts]   = useState<ProductOption[]>([]);
  const [units,      setUnits]      = useState<UnitOption[]>([]);

  // ── UI state ───────────────────────────────────────────────────────────────

  const [loading,     setLoading]     = useState(false);
  const [initialLoad, setInitialLoad] = useState(isEdit);
  const [error,       setError]       = useState<string | null>(null);

  // ── Load dropdowns (+ edit data if editing) ───────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        // Warehouses live under api/v1/organization/warehouses — the short
        // /warehouses path 404s. Products and units use api/v1/[controller],
        // so their short paths resolve correctly.
        const [whRes, prodRes, unitRes] = await Promise.all([
          apiService.get<any>('/organization/warehouses', { pageSize: 200 }),
          apiService.get<any>('/products',    { pageSize: 1000, isActive: true }),
          apiService.get<any>('/units',       { pageSize: 200 }),
        ]);

        const mapWh   = (d: any): WarehouseOption[] =>
          (d?.data?.data ?? d?.data ?? []).map((w: any) => ({
            id:   w.id ?? w.warehouseId,
            name: w.name ?? w.warehouseName,
          }));
        const mapProd = (d: any): ProductOption[] =>
          (d?.data?.data ?? d?.data ?? []).map((p: any) => ({
            id:  p.id ?? p.productId,
            name: p.name ?? p.productName,
            sku:  p.sku ?? p.productCode ?? '',
          }));
        const mapUnit = (d: any): UnitOption[] =>
          (d?.data?.data ?? d?.data ?? []).map((u: any) => ({
            id:       u.id ?? u.unitId,
            unitName: u.unitName ?? u.name,
          }));

        setWarehouses(mapWh(whRes.data));
        setProducts(mapProd(prodRes.data));
        setUnits(mapUnit(unitRes.data));

        // If editing, load the existing reconciliation
        if (isEdit && id) {
          const srRes = await stockReconciliationService.getById(id);
          const sr    = srRes.data?.data;
          if (sr) {
            setWarehouseId(sr.warehouseId);
            setReconciliationDate(sr.reconciliationDate.split('T')[0]);
            setReconciliationType(sr.reconciliationType);
            setNotes(sr.notes ?? '');
            if (sr.items?.length) {
              setItems(
                sr.items.map((item: any) => ({
                  stockReconciliationItemId: item.id,
                  productId:                 item.productId,
                  unitId:                    item.unitId,
                  physicalQuantity:          item.physicalQuantity,
                  varianceReason:            item.varianceReason ?? '',
                  notes:                     item.notes ?? '',
                }))
              );
            }
          }
        }
      } catch {
        setError(t('stockReconciliation.failedToLoad'));
      } finally {
        setInitialLoad(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Item handlers ──────────────────────────────────────────────────────────

  const handleItemChange = (index: number, field: keyof LineItem, value: unknown) => {
    setItems((prev) => {
      const next = [...prev];
      (next[index] as Record<string, unknown>)[field] = value;
      return next;
    });
  };

  const addLine    = () => setItems((prev) => [...prev, emptyLine()]);
  const removeLine = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = (): string | null => {
    if (warehouseId <= 0)   return t('stockReconciliation.warehouseRequired');
    if (!reconciliationDate) return t('stockReconciliation.dateRequired');
    const validItems = items.filter(
      (i) => i.productId > 0 && i.unitId > 0 && i.physicalQuantity >= 0
    );
    if (validItems.length === 0) return t('stockReconciliation.itemsRequired');
    return null;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    const validItems = items.filter(
      (i) => i.productId > 0 && i.unitId > 0 && i.physicalQuantity >= 0
    );

    setLoading(true);
    setError(null);
    try {
      if (isEdit && id) {
        await stockReconciliationService.update(id, {
          warehouseId,
          reconciliationDate,
          reconciliationType,
          notes:            notes || undefined,
          countedByUserId:  user?.id ?? 0,
          items: validItems.map((i) => ({
            productId:        i.productId,
            unitId:           i.unitId,
            physicalQuantity: i.physicalQuantity,
            varianceReason:   i.varianceReason || undefined,
            notes:            i.notes || undefined,
          })),
        });
        navigate(`/stock-reconciliations/${id}`, {
          state: { message: t('stockReconciliation.updatedSuccess') },
        });
      } else {
        const payload: CreateStockReconciliationRequest = {
          warehouseId,
          reconciliationDate,
          reconciliationType,
          notes:           notes || undefined,
          countedByUserId: user?.id ?? 0,
          items: validItems.map((i) => ({
            productId:        i.productId,
            unitId:           i.unitId,
            physicalQuantity: i.physicalQuantity,
            varianceReason:   i.varianceReason || undefined,
            notes:            i.notes || undefined,
          })),
        };
        const res     = await stockReconciliationService.create(payload);
        const created = res.data?.data;
        navigate(`/stock-reconciliations/${created?.id}`, {
          state: {
            message: t('stockReconciliation.createdSuccess', {
              number: created?.reconciliationNumber,
            }),
          },
        });
      }
    } catch (err: unknown) {
      setError(parseApiError(err, isEdit ? t('stockReconciliation.failedToUpdate') : t('stockReconciliation.failedToCreate')).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (initialLoad) {
    return (
      <Layout>
        <div
          className="ims-page sr-page"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}
        >
          <div className="ims-spinner" />
        </div>
      </Layout>
    );
  }

  const selectedWarehouse = warehouses.find((w) => w.id === warehouseId);
  const validItemCount    = items.filter(
    (i) => i.productId > 0 && i.unitId > 0 && i.physicalQuantity >= 0
  ).length;

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="ims-create-body-page sr-page">

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button
              type="button"
              className="ims-back-btn"
              onClick={() => navigate('/stock-reconciliations')}
            >
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('stockReconciliation.backToList')}
            </button>
            <div>
              <h1 className="ims-page-title">
                {isEdit ? t('stockReconciliation.editTitle') : t('stockReconciliation.createTitle')}
              </h1>
              <p className="ims-page-subtitle">
                {isEdit ? t('stockReconciliation.editSubtitle') : t('stockReconciliation.createSubtitle')}
              </p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => navigate('/stock-reconciliations')}
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <>
                  <Save size={14} strokeWidth={2} />
                  {isEdit ? t('common.saveChanges') : t('stockReconciliation.createReconciliation')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error">
            <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
            >
              ✕
            </button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* ─── LEFT — Reconciliation Details ─── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('stockReconciliation.reconciliationDetails')}</h2>
              </div>
              <div className="ims-card-body">

                {/* Warehouse */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('stockReconciliation.warehouse')} <span className="required">*</span>
                  </label>
                  <select
                    className="ims-form-control"
                    aria-label={t('stockReconciliation.warehouse')}
                    value={warehouseId}
                    onChange={(e) => setWarehouseId(Number(e.target.value))}
                  >
                    <option value={0}>{t('stockReconciliation.selectWarehouse')}</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                </div>

                {/* Reconciliation Date */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('stockReconciliation.reconciliationDate')} <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    id="sr-reconciliation-date"
                    className="ims-form-control"
                    aria-label={t('stockReconciliation.reconciliationDate')}
                    value={reconciliationDate}
                    onChange={(e) => setReconciliationDate(e.target.value)}
                    required
                  />
                </div>

                {/* Reconciliation Type */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('stockReconciliation.reconciliationType')} <span className="required">*</span>
                  </label>
                  <select
                    className="ims-form-control"
                    aria-label={t('stockReconciliation.reconciliationType')}
                    value={reconciliationType}
                    onChange={(e) => setReconciliationType(e.target.value)}
                  >
                    {RECONCILIATION_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('common.notes')}</label>
                  <textarea
                    className="ims-form-control ims-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder={t('stockReconciliation.notesPlaceholder')}
                  />
                </div>

                {/* Counted By */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('stockReconciliation.countedBy')}</label>
                  <div className="ims-user-row" style={{ marginTop: 4 }}>
                    <div className="ims-user-avatar">
                      {user?.firstName?.[0]?.toUpperCase() ?? '?'}
                    </div>
                    <div>
                      <div className="ims-user-name">{user?.firstName} {user?.lastName}</div>
                      <div className="ims-user-email">{user?.email}</div>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* ─── CENTER — Items ─── */}
          <div className="ims-panel-center">
            <div className="ims-card ims-lines-card">
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('stockReconciliation.items')}</h2>
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={addLine}
                >
                  <Plus size={12} strokeWidth={2.5} />
                  {t('stockReconciliation.addItem')}
                </button>
              </div>

              <div className="ims-lines-body">
                {items.length === 0 ? (
                  <div className="ims-items-empty">
                    <div className="ims-items-empty-icon">📋</div>
                    {t('stockReconciliation.addItemsHint')}
                  </div>
                ) : (
                  <div className="ims-table-scroll">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th className="ims-th" style={{ minWidth: 200 }}>{t('stockReconciliation.product')}</th>
                          <th className="ims-th" style={{ minWidth: 120 }}>{t('stockReconciliation.unit')}</th>
                          <th className="ims-th" style={{ minWidth: 80, textAlign: 'right' }}>
                            {t('stockReconciliation.systemQuantity')}
                          </th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 120 }}>
                            {t('stockReconciliation.physicalQuantity')}
                          </th>
                          <th className="ims-th" style={{ minWidth: 80, textAlign: 'right' }}>
                            {t('stockReconciliation.variance')}
                          </th>
                          <th className="ims-th" style={{ minWidth: 160 }}>{t('stockReconciliation.varianceReason')}</th>
                          <th className="ims-th" style={{ minWidth: 160 }}>{t('common.notes')}</th>
                          <th className="ims-th ims-th--del" />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          // In create mode system qty is unknown (backend sets it). Show — placeholder.
                          const systemQtyDisplay = isEdit ? '—' : '—';
                          // Variance is physical - system; unknown until saved.
                          const varianceDisplay  = '—';

                          return (
                            <tr key={index} className="ims-tr">
                              {/* Product */}
                              <td className="ims-td">
                                <select
                                  className="ims-cell-input"
                                  aria-label={t('stockReconciliation.product')}
                                  value={item.productId}
                                  onChange={(e) =>
                                    handleItemChange(index, 'productId', Number(e.target.value))
                                  }
                                >
                                  <option value={0}>{t('stockReconciliation.selectProduct')}</option>
                                  {products.map((p) => (
                                    <option key={p.id} value={p.id}>
                                      {p.name}{p.sku ? ` (${p.sku})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              {/* Unit */}
                              <td className="ims-td">
                                <select
                                  className="ims-cell-input"
                                  aria-label={t('stockReconciliation.unit')}
                                  value={item.unitId}
                                  onChange={(e) =>
                                    handleItemChange(index, 'unitId', Number(e.target.value))
                                  }
                                >
                                  <option value={0}>{t('stockReconciliation.selectUnit')}</option>
                                  {units.map((u) => (
                                    <option key={u.id} value={u.id}>{u.unitName}</option>
                                  ))}
                                </select>
                              </td>
                              {/* System Qty (read-only placeholder) */}
                              <td className="ims-td" style={{ textAlign: 'right', color: 'var(--ims-text-muted)', fontSize: '0.875rem' }}>
                                {systemQtyDisplay}
                              </td>
                              {/* Physical Qty */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className="ims-cell-input ims-num-input"
                                  aria-label={t('stockReconciliation.physicalQuantity')}
                                  step="0.01"
                                  min={0}
                                  value={item.physicalQuantity || ''}
                                  onChange={(e) =>
                                    handleItemChange(index, 'physicalQuantity', parseFloat(e.target.value) || 0)
                                  }
                                  placeholder="0"
                                />
                              </td>
                              {/* Variance (computed by backend) */}
                              <td className="ims-td" style={{ textAlign: 'right', color: 'var(--ims-text-muted)', fontSize: '0.875rem' }}>
                                {varianceDisplay}
                              </td>
                              {/* Variance Reason */}
                              <td className="ims-td">
                                <input
                                  type="text"
                                  className="ims-cell-input"
                                  value={item.varianceReason}
                                  onChange={(e) =>
                                    handleItemChange(index, 'varianceReason', e.target.value)
                                  }
                                  placeholder={t('stockReconciliation.varianceReasonPlaceholder')}
                                />
                              </td>
                              {/* Notes */}
                              <td className="ims-td">
                                <input
                                  type="text"
                                  className="ims-cell-input"
                                  value={item.notes}
                                  onChange={(e) =>
                                    handleItemChange(index, 'notes', e.target.value)
                                  }
                                  placeholder={t('stockReconciliation.itemNotesPlaceholder')}
                                />
                              </td>
                              {/* Delete */}
                              <td className="ims-td ims-td--del">
                                <button
                                  type="button"
                                  className="ims-remove-btn"
                                  onClick={() => removeLine(index)}
                                  disabled={items.length === 1}
                                  title={t('common.remove')}
                                >
                                  <X size={12} strokeWidth={2.5} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Hint about system quantity */}
              <div style={{
                padding: '8px 16px 12px',
                fontSize: '0.75rem',
                color: 'var(--ims-text-muted)',
                borderTop: '1px solid var(--ims-border)',
              }}>
                ℹ️ {t('stockReconciliation.systemQtyHint')}
              </div>
            </div>
          </div>

          {/* ─── RIGHT — Summary card ─── */}
          <div className="ims-panel-right">

            {/* Summary */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('stockReconciliation.summary')}</h3>
              </div>
              <div className="ims-card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Warehouse */}
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ims-text-muted)', marginBottom: 4 }}>
                      {t('stockReconciliation.warehouse')}
                    </div>
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: 6,
                      background: warehouseId > 0 ? 'var(--ims-accent-bg)' : 'var(--bg-secondary)',
                      color: warehouseId > 0 ? 'var(--ims-accent)' : 'var(--ims-text-muted)',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}>
                      {selectedWarehouse?.name ?? t('stockReconciliation.noWarehouseSelected')}
                    </div>
                  </div>

                  {/* Type */}
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ims-text-muted)', marginBottom: 4 }}>
                      {t('stockReconciliation.reconciliationType')}
                    </div>
                    <span className="ims-badge" style={{ fontSize: '0.8rem' }}>{reconciliationType}</span>
                  </div>

                  {/* Date */}
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ims-text-muted)', marginBottom: 4 }}>
                      {t('stockReconciliation.reconciliationDate')}
                    </div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--ims-text-main)' }}>
                      {reconciliationDate
                        ? new Date(reconciliationDate).toLocaleDateString()
                        : '—'}
                    </div>
                  </div>

                  {/* Item count */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 12px',
                    borderRadius: 6,
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--ims-border)',
                    marginTop: 4,
                  }}>
                    <span style={{ fontSize: '0.875rem', color: 'var(--ims-text-sub)' }}>
                      {t('stockReconciliation.itemCount')}
                    </span>
                    <span style={{ fontWeight: 700, fontSize: '1.125rem', color: 'var(--ims-accent)' }}>
                      {validItemCount}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tips */}
            <div className="ims-card" style={{ marginTop: 16 }}>
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('stockReconciliation.tipsTitle')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('stockReconciliation.tip1')}</li>
                  <li>{t('stockReconciliation.tip2')}</li>
                  <li>{t('stockReconciliation.tip3')}</li>
                </ul>
              </div>
            </div>

          </div>

        </div>
      </form>
    </Layout>
  );
};

export default StockReconciliationCreatePage;
